/**
 * TierEditor — editable list of label + numeric-value tier rows.
 *
 * Used by CreatePractitionerSection and EditPractitionerModal to configure
 * Other Profit and Other Deduction tiers on a practitioner profile.
 *
 * Props:
 *   title            — section heading ("Other Profit Tiers")
 *   rows             — full rows array from form state
 *   rowsVisible      — how many rows are currently shown
 *   maxRows          — ceiling for "Add tier"
 *   valueField       — key within each row for the numeric value ("percent" | "hour_rate")
 *   valueInputProps  — spread onto the value <input> (type, min, max?, step)
 *   valuePlaceholder — placeholder for the value input
 *   labelPlaceholder — placeholder for the label input
 *   emptyRow         — row shape used when removing the last row
 *   note             — optional hint line shown under the title
 *   errorOk          — from validation meta; shows error message when false
 *   errorMessage     — message shown when !errorOk
 *   submitting       — disables all inputs while a network request is in flight
 *   onRowsChange     — (rows) => void
 *   onVisibleChange  — (n) => void
 *
 * Optional per-row mode selector — for tiers whose value is configured here for
 * some modes and entered later for others (Other Deduction: an $/hr rate lives
 * on the profile, a fixed amount is typed in each month):
 *   modeField        — key within each row holding the mode ("mode"); omit for no selector
 *   modeOptions      — [{ value, label }] shown in the selector
 *   valueHiddenModes — modes where there is no value to configure here
 *   valueHiddenNote  — text shown in the value slot for those modes
 */
export default function TierEditor({
  title,
  rows,
  rowsVisible,
  maxRows,
  valueField,
  valueInputProps,
  valuePlaceholder,
  labelPlaceholder,
  emptyRow,
  errorOk,
  errorMessage,
  submitting,
  onRowsChange,
  onVisibleChange,
  note = "",
  modeField = null,
  modeOptions = [],
  valueHiddenModes = [],
  valueHiddenNote = "",
}) {
  return (
    <div className="sm:col-span-2 space-y-2 rounded-xl border border-palette-gray-divider p-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-palette-gray-heading">{title}</div>
        <button
          type="button"
          className="text-xs text-palette-blue-badge hover:underline disabled:opacity-50"
          disabled={submitting || rowsVisible >= maxRows}
          onClick={() => onVisibleChange(Math.min(maxRows, rowsVisible + 1))}
        >
          + Add tier
        </button>
      </div>

      {note && <p className="text-xs italic text-palette-gray-secondary">{note}</p>}

      {rows.slice(0, rowsVisible).map((row, idx) => {
        const rowMode = modeField ? row[modeField] : null;
        const valueHidden = modeField && valueHiddenModes.includes(rowMode);
        return (
          <div key={idx} className="flex items-center gap-2">
            <span className="w-6 text-xs text-palette-gray-placeholder">{idx + 1}.</span>
            <input
              type="text"
              className="flex-1 rounded-lg border border-palette-gray-divider px-3 py-2 text-sm"
              value={row.label}
              onChange={(e) => {
                const next = [...rows];
                next[idx] = { ...next[idx], label: e.target.value };
                onRowsChange(next);
              }}
              placeholder={labelPlaceholder}
              disabled={submitting}
            />
            {modeField && (
              <select
                className="w-28 rounded-lg border border-palette-gray-divider px-2 py-2 text-sm"
                value={rowMode ?? ""}
                onChange={(e) => {
                  const mode = e.target.value;
                  const next = [...rows];
                  next[idx] = {
                    ...next[idx],
                    [modeField]: mode,
                    // A mode with no value to configure here clears the old one,
                    // so a stale figure can't be saved behind the hidden input.
                    ...(valueHiddenModes.includes(mode) ? { [valueField]: "" } : {}),
                  };
                  onRowsChange(next);
                }}
                disabled={submitting}
              >
                {modeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
            {valueHidden ? (
              <span className="w-28 px-1 text-xs italic text-palette-gray-placeholder">
                {valueHiddenNote}
              </span>
            ) : (
              <input
                className="w-28 rounded-lg border border-palette-gray-divider px-3 py-2 text-sm"
                value={row[valueField]}
                onChange={(e) => {
                  const next = [...rows];
                  next[idx] = { ...next[idx], [valueField]: e.target.value };
                  onRowsChange(next);
                }}
                placeholder={valuePlaceholder}
                disabled={submitting}
                {...valueInputProps}
              />
            )}
            {idx >= 1 && idx === rowsVisible - 1 && (
              <button
                type="button"
                className="text-xs text-palette-red-label hover:text-palette-red-badge hover:underline"
                onClick={() => {
                  const next = [...rows];
                  next[idx] = emptyRow;
                  onRowsChange(next);
                  onVisibleChange(rowsVisible - 1);
                }}
              >
                ✕
              </button>
            )}
          </div>
        );
      })}

      {!errorOk && <div className="mt-1 text-xs text-palette-red-badge">{errorMessage}</div>}
    </div>
  );
}
